import React, { PropsWithChildren, useEffect, useState, createContext, useContext } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, xcss } from '@atlaskit/primitives';
import Tabs, { TabList, useTabPanel, useTab } from '@atlaskit/tabs';

import { ContentBlock } from '@/components/wpl/content-block/src/content-block';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import {
	ATLASSIAN_ISOLATED_CLOUD_PRODUCT,
	isAtlassianIsolatedCloudContactFormEnabled,
} from '../utils';

import { Option } from './option';

export type ProductContext = {
	selectedProduct: string;
};

const SelectedProductContext = createContext<ProductContext>({
	selectedProduct: '',
});

export const useSelectedProduct = () => {
	const context = useContext(SelectedProductContext);
	if (!context) {
		throw new Error('useSelectedProduct must be used within a SelectedProductProvider');
	}
	return context;
};

export const SelectedProductProvider = (props: PropsWithChildren<ProductContext>) => {
	return (
		<SelectedProductContext.Provider value={{ selectedProduct: props.selectedProduct }}>
			{props.children}
		</SelectedProductContext.Provider>
	);
};

interface ProductSelectorProps {
	onChangeHandler?: (selected: string, platform: string) => void;
	onChange?: (value: string) => void;
	pageType?: 'pricing-quotes' | 'migration-options' | 'product-features';
}

const prefillableProducts: { [key: string]: string } = {
	guard: 'Atlassian Guard',
	bitbucket: 'Bitbucket',
	compass: 'Compass',
	confluence: 'Confluence',
	dx: 'DX',
	jira: 'Jira',
	'jira-align': 'Jira Align',
	'jira-product-discovery': 'Jira Product Discovery',
	'jira-service-management': 'Jira Service Management',
	'jira-service-management-assets': 'Jira Service Management Assets',
	'jira-service-management-virtual-service-agent': 'Jira Service Management Virtual Service Agent',
	loom: 'Loom',
	rovo: 'Rovo',
	trello: 'Trello',
};
const cloudPlatform = 'Cloud';

const customTabStyles = xcss({
	alignItems: 'flex-start',
	boxSizing: 'border-box',
	display: 'block',
	fontFamily: `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
	fontFeatureSettings: 'normal',
	fontKerning: 'auto',
	fontOpticalSizing: 'auto',
	fontSize: '16px',
	fontStretch: '100%',
	fontStyle: 'normal',
	fontVariationSettings: 'normal',
	fontWeight: '400',
	letterSpacing: 'normal',
	lineHeight: 'normal',
	textAlign: 'center',
	textIndent: '0px',
	textShadow: 'none',
	textTransform: 'none',
	wordSpacing: '0px',
});

const getDataEventProps = (text?: string, key?: string) => {
	return {
		'data-label': text || '',
		'data-product-key': key || '',
		'data-event-component': 'link',
		'data-event-container': 'productSelector',
		'data-event': 'clicked',
		'data-label-english': text,
	};
};

const CustomTab = ({ label }: { label: string }) => {
	const tabAttributes = useTab();

	return (
		<Box xcss={customTabStyles} {...tabAttributes}>
			{label}
		</Box>
	);
};

export const dcPlatform = 'Data Center';

export const dataCenterProductsLinkButtonProps = [
	{
		key: 'jira-software',
		text: 'Jira Software',
		dataEventProps: {
			'data-product-key': 'jiraSoftware',
		},
	},
	{
		key: 'confluence',
		text: 'Confluence',
		dataEventProps: {
			'data-product-key': 'confluence',
		},
	},
	{
		key: 'jira-service-management',
		text: 'Jira Service Management',
		dataEventProps: {
			'data-product-key': 'jiraServiceManagement',
		},
	},
	{
		key: 'bitbucket',
		text: 'Bitbucket',
		dataEventProps: {
			'data-product-key': 'bitbucket',
		},
	},
	{
		key: 'bamboo',
		text: 'Bamboo',
		dataEventProps: {
			'data-product-key': 'bamboo',
		},
	},
	{
		key: 'crowd',
		text: 'Crowd',
		dataEventProps: {
			'data-product-key': 'crowd',
		},
	},
];

const DCTab = ({
	onClick,
}: {
	onClick: (platform: string) => (event: React.MouseEvent<HTMLElement>) => void;
}) => {
	const intl = useIntl();

	const premierSupportText = intl.formatMessage({
		id: 'migrationProductSelector.premierSupport',
		defaultMessage: 'Premier Support',
	});

	const prioritySupportText = intl.formatMessage({
		id: 'migrationProductSelector.prioritySupport',
		defaultMessage: 'Priority Support',
	});

	const professionalServicesText = intl.formatMessage({
		id: 'migrationProductSelector.professionalServices',
		defaultMessage: 'Professional Services',
	});

	const tabPanelAttributes = useTabPanel();

	const dataCenterProductsText = intl.formatMessage({
		id: 'migrationProductSelector.dataCenterProducts',
		defaultMessage: 'Data Center Products',
	});

	const dataCenterServicesLinkButtonProps = [
		{
			key: 'premier-support',
			// text is internationalized here, but we don't want to use the internalized text
			// for the data-label-english attribute; but do we want to use it for the data-label
			// attribute? i think we should use the internalized text for the data-label attribute
			// but currently in prod it isn't internationalized
			text: premierSupportText,
			dataEventProps: {
				'data-label': 'Premier Support',
				'data-label-english': 'Premier Support',
				'data-product-key': 'support',
			},
		},
		{
			key: 'priority-support',
			text: prioritySupportText,
			dataEventProps: {
				'data-label': 'Priority Support',
				'data-label-english': 'Priority Support',
				'data-product-key': 'support',
			},
		},
		{
			key: 'advisory-services',
			text: professionalServicesText,
			dataEventProps: {
				'data-label': 'Professional Services',
				'data-label-english': 'Professional Services',
				'data-product-key': 'support',
			},
		},
	];

	const servicesText = intl.formatMessage({
		id: 'migrationProductSelector.services',
		defaultMessage: 'Services',
	});

	return (
		<div {...tabPanelAttributes}>
			<ContentBlock
				headerType={'h5'}
				headerText={dataCenterProductsText}
				bodyComponent={
					<>
						{dataCenterProductsLinkButtonProps.map(({ key, text, dataEventProps }) => (
							<Option
								key={key}
								{...getDataEventProps(text, key)}
								{...dataEventProps}
								onClick={onClick(dcPlatform)}
							>
								{text}
							</Option>
						))}
					</>
				}
			/>
			<ContentBlock
				headerText={servicesText}
				headerType={'h5'}
				bodyComponent={
					<>
						{dataCenterServicesLinkButtonProps.map(({ key, text, dataEventProps }) => (
							<Option
								key={key}
								{...getDataEventProps(text, key)}
								{...dataEventProps}
								onClick={onClick(dcPlatform)}
							>
								{text}
							</Option>
						))}
					</>
				}
			/>
			<div css={css({ marginBlockEnd: '20px' })} />
		</div>
	);
};

export const usagePacksPlatform = 'Usage Packs';

/**
 * Usage-pack products, in the fixed display order defined by REQ-ENT-UI-04.02.
 * Exported so the form sections can detect when a usage pack is selected (to
 * show the self-serve help panel + estimated-usage field).
 */
export const usagePackProductsLinkButtonProps = [
	{
		key: 'assets-objects-pack',
		text: 'Assets objects pack',
		dataEventProps: { 'data-product-key': 'assetsObjectsPack' },
	},
	{
		key: 'automation-steps-pack',
		text: 'Automation steps pack',
		dataEventProps: { 'data-product-key': 'automationStepsPack' },
	},
	{
		key: 'bitbucket-build-minutes-pack',
		text: 'Bitbucket build minutes pack',
		dataEventProps: { 'data-product-key': 'bitbucketBuildMinutesPack' },
	},
	{
		key: 'bitbucket-git-lfs-pack',
		text: 'Bitbucket Git LFS pack',
		dataEventProps: { 'data-product-key': 'bitbucketGitLfsPack' },
	},
	{
		key: 'bitbucket-package-network-pack',
		text: 'Bitbucket package network pack',
		dataEventProps: { 'data-product-key': 'bitbucketPackageNetworkPack' },
	},
	{
		key: 'bitbucket-package-storage-pack',
		text: 'Bitbucket package storage pack',
		dataEventProps: { 'data-product-key': 'bitbucketPackageStoragePack' },
	},
	{
		key: 'csm-ai-agent-resolutions-pack',
		text: 'Customer Service Management AI agent resolutions pack',
		dataEventProps: { 'data-product-key': 'csmAiAgentResolutionsPack' },
	},
	{
		key: 'rovo-credit-pack',
		text: 'Rovo credit pack',
		dataEventProps: { 'data-product-key': 'rovoCreditPack' },
	},
];

/**
 * The set of display names that count as "usage pack" products. Used by the
 * form sections to conditionally render the help panel + estimated-usage field.
 */
export const usagePackProductNames = usagePackProductsLinkButtonProps.map(({ text }) => text);

export const isUsagePackProduct = (product: string): boolean =>
	usagePackProductNames.includes(product);

const UsagePacksTab = ({
	onClick,
}: {
	onClick: (platform: string) => (event: React.MouseEvent<HTMLElement>) => void;
}) => {
	const intl = useIntl();
	const tabPanelAttributes = useTabPanel();

	const usagePacksText = intl.formatMessage({
		id: 'migrationProductSelector.usagePacks',
		defaultMessage: 'Usage Packs',
	});

	return (
		<div {...tabPanelAttributes}>
			<ContentBlock
				headerType={'h5'}
				headerText={usagePacksText}
				bodyComponent={
					<>
						{usagePackProductsLinkButtonProps.map(({ key, text, dataEventProps }) => (
							<Option
								key={key}
								{...getDataEventProps(text, key)}
								{...dataEventProps}
								onClick={onClick(usagePacksPlatform)}
							>
								{text}
							</Option>
						))}
					</>
				}
			/>
			<div css={css({ marginBlockEnd: '20px' })} />
		</div>
	);
};

const CloudTab = ({
	pageType,
	onClick,
}: {
	pageType: 'pricing-quotes' | 'migration-options' | 'product-features';
	onClick: (platform: string) => (event: React.MouseEvent<HTMLElement>) => void;
	// eslint-disable-next-line complexity
}) => {
	const intl = useIntl();
	const removeRovoFromPricingPage = checkFeatureFlagGate(
		featureFlags.gwp5682RemoveRovoFromPricingForm,
	);

	const isOpsgenieEOL = checkFeatureFlagGate(featureFlags.gwp6474RemoveOpsgenieContactSalesForm);

	const isTwCEnabled = checkFeatureFlagGate(featureFlags.gwpEnterpriseRolloutGateTwc);

	const isStrategyCollectionEnabled = checkFeatureFlagGate(
		featureFlags.gwpEnterpriseRolloutGateStrategyCollection,
	);

	const isServiceCollectionEnabled = checkFeatureFlagGate(
		featureFlags.gwp8165EnterpriseContactServiceCollection,
	);

	const isBackupRestoreEnabled = checkFeatureFlagGate(featureFlags.gwp9763BackupRestoreContactForm);

	const isAdditionalSandboxEnabled = checkFeatureFlagGate(
		featureFlags.gwp9608AdditionalSandboxContactForm,
	);

	const isAtlassianIsolatedCloudEnabled = isAtlassianIsolatedCloudContactFormEnabled();

	const sunsetCompass = checkFeatureFlagGate(featureFlags.gwp1072SunsetCompass);

	const cloudProductsText = intl.formatMessage({
		id: 'migrationProductSelector.cloudProducts',
		defaultMessage: 'Cloud Products',
	});

	const servicesText = intl.formatMessage({
		id: 'migrationProductSelector.services',
		defaultMessage: 'Services',
	});

	const professionalServicesText = intl.formatMessage({
		id: 'migrationProductSelector.professionalServices',
		defaultMessage: 'Professional Services',
	});

	const cloudProductsLinkButtonProps = [
		{
			key: 'atlassian-access',
			text: 'Atlassian Guard',
			dataEventProps: {
				'data-product-key': 'atlassianGuard',
			},
		},
		...(isAtlassianIsolatedCloudEnabled
			? [
					{
						key: 'atlassian-isolated-cloud',
						text: ATLASSIAN_ISOLATED_CLOUD_PRODUCT,
						dataEventProps: {
							'data-product-key': 'atlassianIsolatedCloud',
						},
					},
				]
			: []),
		{
			key: 'bitbucket',
			text: 'Bitbucket',
			dataEventProps: {
				'data-product-key': 'bitbucket',
			},
		},
		...(!sunsetCompass
			? [
					{
						key: 'compass',
						text: 'Compass',
						dataEventProps: {
							'data-product-key': 'compass',
						},
					},
				]
			: []),
		{
			key: 'confluence',
			text: 'Confluence',
			dataEventProps: {
				'data-product-key': 'confluence',
			},
		},
		...(isBackupRestoreEnabled
			? [
					{
						key: 'confluence-cloud-backup-restore',
						text: 'Confluence Cloud Backup and Restore',
						dataEventProps: {
							'data-product-key': 'confluenceCloudBackupRestore',
						},
					},
				]
			: []),
		...(isAdditionalSandboxEnabled
			? [
					{
						key: 'confluence-additional-sandbox',
						text: 'Confluence Additional Sandbox',
						dataEventProps: {
							'data-product-key': 'confluenceAdditionalSandbox',
						},
					},
				]
			: []),
		{
			key: 'dx',
			text: 'DX',
			dataEventProps: {
				'data-product-key': 'dx',
			},
		},
		{
			key: 'jira',
			text: 'Jira',
			dataEventProps: {
				'data-product-key': 'jira',
			},
		},
		...(!isTwCEnabled
			? [
					{
						key: 'jira-align',
						text: 'Jira Align',
						dataEventProps: {
							'data-product-key': 'jiraAlign',
						},
					},
				]
			: []),
		...(isBackupRestoreEnabled
			? [
					{
						key: 'jira-cloud-backup-restore',
						text: 'Jira Cloud Backup and Restore',
						dataEventProps: {
							'data-product-key': 'jiraCloudBackupRestore',
						},
					},
				]
			: []),
		{
			key: 'jira-product-discovery',
			text: 'Jira Product Discovery',
			dataEventProps: {
				'data-product-key': 'jiraProductDiscovery',
			},
		},
		{
			key: 'jira-service-management',
			text: 'Jira Service Management',
			dataEventProps: {
				'data-product-key': 'jiraServiceManagement',
			},
		},
		{
			key: 'jira-service-management-assets',
			text: 'Jira Service Management Assets',
			dataEventProps: {
				'data-product-key': 'jiraServiceManagementAssets',
			},
		},
		{
			key: 'jira-service-management-virtual-service-agent',
			text: 'Jira Service Management Virtual Service Agent',
			dataEventProps: {
				'data-product-key': 'jiraServiceManagementVirtualServiceAgent',
			},
		},
		...(isBackupRestoreEnabled
			? [
					{
						key: 'jira-service-management-cloud-backup-restore',
						text: 'Jira Service Management Cloud Backup and Restore',
						dataEventProps: {
							'data-product-key': 'jiraServiceManagementCloudBackupRestore',
						},
					},
				]
			: []),
		...(isAdditionalSandboxEnabled
			? [
					{
						key: 'jira-additional-sandbox',
						text: 'Jira Additional Sandbox',
						dataEventProps: {
							'data-product-key': 'jiraAdditionalSandbox',
						},
					},
				]
			: []),
		...(isAdditionalSandboxEnabled
			? [
					{
						key: 'jira-product-discovery-additional-sandbox',
						text: 'Jira Product Discovery Additional Sandbox',
						dataEventProps: {
							'data-product-key': 'jiraProductDiscoveryAdditionalSandbox',
						},
					},
				]
			: []),
		...(isAdditionalSandboxEnabled
			? [
					{
						key: 'jira-service-management-additional-sandbox',
						text: 'Jira Service Management Additional Sandbox',
						dataEventProps: {
							'data-product-key': 'jiraServiceManagementAdditionalSandbox',
						},
					},
				]
			: []),
		{
			key: 'loom',
			text: 'Loom',
			dataEventProps: {
				'data-product-key': 'loom',
			},
		},
		...(!isOpsgenieEOL
			? [
					{
						key: 'opsgenie',
						text: 'Opsgenie',
						dataEventProps: {
							'data-product-key': 'opsgenie',
						},
					},
				]
			: []),
		...(!removeRovoFromPricingPage || pageType !== 'pricing-quotes'
			? [
					{
						key: 'rovo',
						text: 'Rovo',
						dataEventProps: {
							'data-product-key': 'rovo',
						},
					},
				]
			: []),
		...(isServiceCollectionEnabled
			? [
					{
						key: 'service-collection',
						text: 'Service Collection',
						dataEventProps: {
							'data-product-key': 'serviceCollection',
						},
					},
				]
			: []),
		...(isStrategyCollectionEnabled
			? [
					{
						key: 'strategy-collection',
						text: 'Strategy Collection',
						dataEventProps: {
							'data-product-key': 'strategyCollection',
						},
					},
				]
			: []),
		...(isTwCEnabled
			? [
					{
						key: 'teamwork-collection',
						text: 'Teamwork Collection',
						dataEventProps: {
							'data-product-key': 'teamworkCollection',
						},
					},
				]
			: []),
		{
			key: 'trello',
			text: 'Trello',
			dataEventProps: {
				'data-product-key': 'trello',
			},
		},
	];

	const tabPanelAttributes = useTabPanel();

	return (
		<div {...tabPanelAttributes}>
			<ContentBlock
				headerType={'h5'}
				orientation={'vertical'}
				headerText={cloudProductsText}
				bodyComponent={
					<>
						{cloudProductsLinkButtonProps.map(({ key, text, dataEventProps }) => (
							<Option
								key={key}
								{...getDataEventProps(text, key)}
								{...dataEventProps}
								onClick={onClick(cloudPlatform)}
							>
								{text}
							</Option>
						))}
					</>
				}
			/>

			<ContentBlock
				headerText={servicesText}
				headerType={'h5'}
				orientation={'vertical'}
				bodyComponent={
					<>
						<Option
							key={'advisoryServices'}
							{...getDataEventProps(professionalServicesText, 'advisoryServices')}
							{...{
								'data-label': 'Professional Services',
								'data-label-english': 'Professional Services',
							}}
							onClick={onClick(cloudPlatform)}
						>
							{professionalServicesText}
						</Option>
					</>
				}
			/>
			<div css={css({ marginBlockEnd: '20px' })} />
		</div>
	);
};

export const ProductSelector = ({ onChangeHandler, onChange, pageType }: ProductSelectorProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const toggleOpen = () => {
		setIsOpen(!isOpen);
	};
	const [selectedOption, setSelectedOption] = useState('');

	useEffect(() => {
		if (selectedOption === '') {
			const urlParams = new URLSearchParams(window.location.search);
			const initialProduct = urlParams.get('product')?.toLowerCase();

			if (initialProduct && prefillableProducts[initialProduct]) {
				const displayLabel = prefillableProducts[initialProduct];
				setSelectedOption(displayLabel);
				// Call the field's registered onChange so final-form records
				// the value in its form state (needed for payload + validation)
				onChange?.(displayLabel);
				onChangeHandler?.(displayLabel, cloudPlatform);
			}
		}
	}, [onChange, onChangeHandler, selectedOption]);

	const productSelectorButtonStyle = [
		isOpen &&
			css({
				borderColor: 'transparent',
				borderRadius: '4px',
				cursor: 'pointer',
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				width: '100%',
				padding: '0',
				alignItems: 'center',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'> .selected-option': {
					lineHeight: '2',
					fontSize: '1rem',
					paddingInlineStart: '1rem',
					textDecoration: 'bold',
					fontFamily:
						"'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'.arrow-down-icon': {
					alignItems: 'right',
					transform: 'rotate(180deg)',
				},
			}),
		!isOpen &&
			css({
				borderColor: 'transparent',
				borderRadius: '4px',
				cursor: 'pointer',
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				width: '100%',
				padding: '0',
				alignItems: 'center',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'> .selected-option': {
					lineHeight: '2',
					fontSize: '1rem',
					paddingInlineStart: '1rem',
					textDecoration: 'bold',
					fontFamily:
						"'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'.arrow-down-icon': {
					alignItems: 'right',
				},
			}),
	];

	const productSelectorStyle = [
		isOpen &&
			css({
				display: 'flex',
				flexDirection: 'column',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'*>[hidden]': {
					display: 'none !important',
				},
				width: '100%',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'[role=tabpanel]': {
					display: 'flex',
					flexDirection: 'column',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'[class*=-Tabs]': {
					marginInlineStart: '.9rem',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'[role=tab]': {
					fontSize: '1rem',
					fontWeight: '400',
					padding: '15px 8px',
					letterSpacing: 'normal',
					lineHeight: 'normal',
					color: 'black',

					alignItems: 'flex-start',
					boxSizing: 'border-box',
					display: 'block',
					fontFeatureSettings: 'normal',
					fontKerning: 'auto',
					fontOpticalSizing: 'auto',
					fontStretch: '100%',
					fontStyle: 'normal',
					textAlign: 'center',
					textIndent: '0px',
					textShadow: 'none',
					textTransform: 'none',
					wordSpacing: '0px',

					'&:hover': {
						// Note: Existing tech debt, should be fixed
						// eslint-disable-next-line local/ensure-wac-token-usage
						color: '#0052cc',
					},

					'&:focus': {
						// Note: Existing tech debt, should be fixed
						// eslint-disable-next-line local/ensure-wac-token-usage
						borderColor: '#0052cc',
						cursor: 'pointer',
						borderStyle: 'solid',
						padding: '15px 8px',
						borderWidth: '1px 1px 2px 1px',
					},
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'.text-section': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
					h5: {
						padding: '1rem 0',
						margin: '0',
						// Note: Existing tech debt, should be fixed
						// eslint-disable-next-line local/ensure-wac-token-usage
						color: '#8993A4',
						fontFamily: `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
						fontWeight: 500,
						textTransform: 'uppercase',
						letterSpacing: '1px',
						lineHeight: '12px',
						fontSize: '.75rem',
					},
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
					'.contentBlock-body': {
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.link-wrapper': {
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
							padding: '8px 0 8px',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							a: {
								fontWeight: 400,
								margin: '0',
								padding: '0',
								'&:hover': {
									color: 'black',
									textShadow: 'none',
									textDecoration: 'underline',
								},
							},
						},
						marginBlockEnd: '0px',
					},
				},
			}),
		!isOpen &&
			css({
				display: 'none',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'*>[hidden]': {
					display: 'none !important',
				},
				width: '100%',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'[role=tabpanel]': {
					display: 'flex',
					flexDirection: 'column',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'.text-section': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
					h5: {
						padding: '1rem 0',
						margin: '0',
						// Note: Existing tech debt, should be fixed
						// eslint-disable-next-line local/ensure-wac-token-usage
						color: '#8993A4',
						fontFamily: `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
						fontWeight: 500,
						textTransform: 'uppercase',
						letterSpacing: '1px',
						lineHeight: '12px',
						fontSize: '.75rem',
					},
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
					'.contentBlock-body': {
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.link-wrapper': {
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							a: {
								fontWeight: 400,
								margin: '0',
								padding: '0',
								'&:hover': {
									color: 'black',
									textShadow: 'none',
									textDecoration: 'underline',
								},
							},
						},
						marginBlockEnd: '0px',
					},
				},
			}),
	];

	const divCss = css({
		fontFamily:
			"'Charlie Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Noto Sans', 'Ubuntu', 'Droid Sans', 'Helvetica Neue', sans-serif",
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%',
	});

	const onClick = (platform: string) => (event: React.MouseEvent<HTMLElement>) => {
		const selected = String(event.currentTarget.dataset['label']);
		setSelectedOption(selected);
		onChangeHandler?.(selected, platform);
		toggleOpen();
	};

	const usagePacksEnabled = checkFeatureFlagGate(featureFlags.gwp12227EnterpriseUsagePacks);

	const tabs = (
		<Tabs id={'product-selector'}>
			<TabList>
				<CustomTab label="Cloud" />
				<CustomTab label="Data Center" />
				{usagePacksEnabled && <CustomTab label="Usage Packs" />}
			</TabList>
			<CloudTab pageType={pageType || 'product-features'} onClick={onClick} />
			<DCTab onClick={onClick} />
			{usagePacksEnabled && <UsagePacksTab onClick={onClick} />}
		</Tabs>
	);

	return (
		<div className={'product-selector'} css={divCss}>
			<button type={'button'} css={productSelectorButtonStyle} onClick={toggleOpen}>
				<span className={'selected-option'}>{selectedOption}</span>
				<span className={'arrow-down-icon'}>
					<ChevronDownIcon label={isOpen ? 'close menu' : 'open menu'} />
				</span>
			</button>
			<div css={productSelectorStyle}>{tabs}</div>
		</div>
	);
};
